import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
    TextField, Grid, CircularProgress,
    Snackbar, Alert, MenuItem, IconButton,
    Select, OutlinedInput, Chip, Box, Autocomplete,
    FormControl, InputLabel, Divider,
} from '@mui/material';
import { ArrowBack, Save as SaveIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { invoiceService } from '../../../shared/services/invoiceService';
import { clientService } from '../../../shared/services/clientService';
import { serviceService } from '../../../shared/services/serviceService';
import { InvoiceFormData, InvoiceService, GstSlab, GST_SLAB_OPTIONS, InvoiceType, INVOICE_TYPE_OPTIONS } from '../../../shared/types/invoice';
import { Client } from '../../../shared/types/client';
import { ServiceItem } from '../../../shared/types/service';

const YEAR_OPTIONS = ['1 Year', '2 Year', '3 Year', '5 Year', '10 Years'];
const REMINDER_OPTIONS = ['Before 2 Months', 'Before 1 Months', 'Before 15 Days', 'Before 1 Week', 'Before 1 Day'];
const VALID_GST_SLABS: ReadonlySet<GstSlab> = new Set(['IGST_0', 'IGST_18', 'CGST_SGST_9']);

const normalizeGstSlab = (raw: unknown): GstSlab | '' => {
    if (raw == null) return '';

    const value = String(raw).trim();
    if (!value) return '';
    if (VALID_GST_SLABS.has(value as GstSlab)) return value as GstSlab;

    const normalized = value
        .toUpperCase()
        .replace(/[%]/g, '')
        .replace(/[+\-]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/__+/g, '_')
        .replace(/^_+|_+$/g, '');

    const map: Record<string, GstSlab> = {
        IGST_0: 'IGST_0',
        IGST0: 'IGST_0',
        NO_GST: 'IGST_0',
        NONE: 'IGST_0',
        GST_0: 'IGST_0',
        IGST_18: 'IGST_18',
        IGST18: 'IGST_18',
        GST_18: 'IGST_18',
        CGST_SGST_9: 'CGST_SGST_9',
        CGST_SGST9: 'CGST_SGST_9',
        CGST9_SGST9: 'CGST_SGST_9',
        CGST_9_SGST_9: 'CGST_SGST_9',
    };

    return map[normalized] || '';
};

const normalizeInvoiceType = (raw: unknown): InvoiceType => {
    const value = String(raw ?? '').trim().toUpperCase();
    return value === 'CF' ? 'CF' : 'INV';
};

const normalizeYearForReminder = (raw: unknown): string[] => {
    const toYearLabel = (value: unknown): string => {
        const text = String(value ?? '').trim();
        if (!text) return '';
        if (/^\d+$/.test(text)) {
            const year = Number(text);
            return year > 0 ? `${year} Year${year > 1 ? 's' : ''}` : '';
        }
        return text;
    };

    if (Array.isArray(raw)) {
        return raw.map(toYearLabel).filter(Boolean);
    }

    const label = toYearLabel(raw);
    if (!label) return [];
    return label.includes(',') ? label.split(',').map(s => s.trim()).filter(Boolean) : [label];
};

const getTaxBreakdown = (amount: number, slab: GstSlab | '', invoiceType: InvoiceType) => {
    if (invoiceType === 'CF' || slab === '' || slab === 'IGST_0') {
        return {
            igstPercent: 0,
            cgstPercent: 0,
            sgstPercent: 0,
            igstAmount: 0,
            cgstAmount: 0,
            sgstAmount: 0,
            taxAmount: 0,
            totalAmount: amount,
        };
    }

    if (slab === 'IGST_18') {
        const igstAmount = parseFloat((amount * 0.18).toFixed(2));
        return {
            igstPercent: 18,
            cgstPercent: 0,
            sgstPercent: 0,
            igstAmount,
            cgstAmount: 0,
            sgstAmount: 0,
            taxAmount: igstAmount,
            totalAmount: parseFloat((amount + igstAmount).toFixed(2)),
        };
    }

    const cgstAmount = parseFloat((amount * 0.09).toFixed(2));
    const sgstAmount = parseFloat((amount * 0.09).toFixed(2));
    const taxAmount = parseFloat((cgstAmount + sgstAmount).toFixed(2));
    return {
        igstPercent: 0,
        cgstPercent: 9,
        sgstPercent: 9,
        igstAmount: 0,
        cgstAmount,
        sgstAmount,
        taxAmount,
        totalAmount: parseFloat((amount + taxAmount).toFixed(2)),
    };
};

const emptyService = (): InvoiceService => ({
    description: '',
    amount: 0,
    ratePerUnitHour: '',
    unitHour: '',
    taxSlab: '',
});

const todayISO = () => new Date().toISOString().split('T')[0];
const normalizeServiceDescription = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

const EMPTY_FORM: InvoiceFormData = {
    invoiceType: 'INV',
    clientId: '',
    services: [emptyService()],
    totalTax: 0,
    totalDiscount: 0,
    advancePaidAmount: 0,
    totalAmount: 0,
    invoiceDate: todayISO(),
    yearForReminder: [],
    optionForReminder: [],
    additionalNote: '',
};

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#fff',
        '& fieldset': { borderColor: '#e2e8f0' },
        '&:hover fieldset': { borderColor: '#cbd5e1' },
        '&.Mui-focused fieldset': { borderColor: '#0EA5E9' },
    },
    '& .MuiInputBase-input': {
        color: '#1f2937',
        fontSize: '15px',
        // Hide up/down arrows (spinners) for type="number"
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            'WebkitAppearance': 'none',
            margin: 0,
        },
        '&[type=number]': {
            'MozAppearance': 'textfield',
        },
    },
};

export const AddEditInvoice: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id?: string }>();
    const isAdd = !id;
    const MAX_SERVICES = 5;
    const backTo = !isAdd && location.state?.from === 'view' ? `/invoices/view/${id}` : '/invoices';
    const viewBackTo = (location.state as { backTo?: string } | null)?.backTo || '/invoices';
    const backState = !isAdd && location.state?.from === 'view' ? { backTo: viewBackTo } : undefined;

    const [form, setForm] = useState<InvoiceFormData>(EMPTY_FORM);
    const [selectedGstSlab, setSelectedGstSlab] = useState<GstSlab | ''>('');
    const [existingInvoiceNumber, setExistingInvoiceNumber] = useState<string>('');
    // Editable sequence part only, e.g. "0001"
    const [invoiceSeq, setInvoiceSeq] = useState<string>('');
    const [nextNumberLoading, setNextNumberLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
    const [isSalesInvoice, setIsSalesInvoice] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', message: '' });
    const [clients, setClients] = useState<Client[]>([]);
    const [serviceOptions, setServiceOptions] = useState<ServiceItem[]>([]);
    const [isServiceOptionsLoading, setIsServiceOptionsLoading] = useState(false);

    const fetchClients = useCallback(async () => {
        try {
            const res = await clientService.list({ limit: 500 });
            const outerData = res?.data;
            const items = outerData?.data || outerData?.items || (outerData as any)?.clients || [];
            setClients((Array.isArray(items) ? items : []).map((c: any) => ({ ...c, _id: c._id ?? c.id })));
        } catch {
            setClients([]);
        }
    }, []);

    useEffect(() => { fetchClients(); }, [fetchClients]);

    const fetchServiceOptions = useCallback(async () => {
        setIsServiceOptionsLoading(true);
        try {
            const res = await serviceService.list({ page: 1, limit: 500 });
            const outerData = res?.data;
            const items =
                outerData?.data ||
                outerData?.items ||
                (outerData as any)?.services ||
                [];
            const mapped = (Array.isArray(items) ? items : []).map((item: any) => ({
                ...item,
                _id: item._id ?? item.id,
                description: String(item.description || '').trim(),
            })).filter((item: ServiceItem) => Boolean(item._id) && Boolean(item.description));
            setServiceOptions(mapped);
        } catch {
            setServiceOptions([]);
        } finally {
            setIsServiceOptionsLoading(false);
        }
    }, []);

    useEffect(() => { fetchServiceOptions(); }, [fetchServiceOptions]);

    useEffect(() => {
        if (id) loadInvoice(id);
    }, [id]);

    // Fetch next number whenever type or date changes (add mode only)
    const fetchNextNumber = useCallback(async (type: string, date: string) => {
        if (!date) return;
        setNextNumberLoading(true);
        try {
            const res = await invoiceService.getNextNumber(type, date);
            const num = res?.data?.nextNumber || '0001';
            setInvoiceSeq(num);
        } catch {
            // keep existing value on error
        } finally {
            setNextNumberLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAdd) {
            fetchNextNumber(form.invoiceType, form.invoiceDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.invoiceType, form.invoiceDate, isAdd]);

    const loadInvoice = async (invoiceId: string) => {
        setIsLoadingInvoice(true);
        try {
            const res = await invoiceService.getById(invoiceId);
            const inv = res.data?.invoice || res.data?.result || (res as any)?.data;
            if (inv) {
                const type: InvoiceType = normalizeInvoiceType(inv.invoiceType ?? inv.type);
                const resolvedGstSlab = normalizeGstSlab(inv.gstSlab ?? inv.services?.[0]?.taxSlab);
                setSelectedGstSlab(type === 'CF' ? '' : resolvedGstSlab);
                setIsSalesInvoice(!!inv.moveToSaleType || (typeof inv.totalPendingAmount === 'number' && inv.totalPendingAmount <= 0));
                setExistingInvoiceNumber(inv.invoiceNumber || '');
                // pre-fill the editable seq from existing invoice number, e.g. "INV-0003/25-26" -> "0003"
                if (inv.invoiceNumber) {
                    const seqMatch = inv.invoiceNumber.match(/-(\d+)\//);
                    setInvoiceSeq(seqMatch ? seqMatch[1] : '');
                }
                setForm({
                    invoiceType: type,
                    clientId: typeof inv.clientId === 'object' ? (inv.clientId?._id || (inv.clientId as any)?.id || '') : (inv.clientId || ''),
                    services: inv.services?.length
                        ? inv.services.map((s: any) => ({
                            ...s,
                            ratePerUnitHour: s.ratePerUnitHour != null ? String(s.ratePerUnitHour) : '',
                            unitHour: s.unitHour != null ? String(s.unitHour) : '',
                            taxSlab: normalizeGstSlab(s.taxSlab ?? resolvedGstSlab),
                        }))
                        : [emptyService()],
                    totalTax: inv.totalTax || 0,
                    totalDiscount: inv.totalDiscount || 0,
                    advancePaidAmount: inv.advancePaidAmount || 0,
                    totalAmount: inv.totalAmount || 0,
                    invoiceDate: inv.invoiceDate ? inv.invoiceDate.split('T')[0] : todayISO(),
                    yearForReminder: normalizeYearForReminder(inv.yearForReminder),
                    optionForReminder: Array.isArray(inv.optionForReminder) ? inv.optionForReminder : (inv.optionForReminder ? String(inv.optionForReminder).split(',').map((s: string) => s.trim()) : []),
                    additionalNote: inv.additionalNote || '',
                });
            }
        } catch (err: any) {
            setAlert({ show: true, type: 'error', message: err.response?.data?.message || 'Error loading invoice details' });
        } finally {
            setIsLoadingInvoice(false);
        }
    };

    // Auto-calculate totals whenever services, selectedGstSlab, totalDiscount change
    useEffect(() => {
        const tax = form.services.reduce((sum, s) => {
            const amount = Number(s.amount) || 0;
            const slab = form.invoiceType === 'CF' ? '' : (s.taxSlab || selectedGstSlab);
            return sum + getTaxBreakdown(amount, slab, form.invoiceType).taxAmount;
        }, 0);
        const servicesTotal = form.services.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
        const total = servicesTotal + tax - (Number(form.totalDiscount) || 0);

        setForm(prev => ({
            ...prev,
            totalTax: parseFloat(tax.toFixed(2)),
            totalAmount: parseFloat(Math.max(0, total).toFixed(2)),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.services, selectedGstSlab, form.totalDiscount, form.invoiceType]);

    // GST breakdown for display
    const servicesTotal = form.services.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
    const cgstAmount = form.services.reduce((sum, s) => {
        const amount = Number(s.amount) || 0;
        const slab = form.invoiceType === 'CF' ? '' : (s.taxSlab || selectedGstSlab);
        return sum + getTaxBreakdown(amount, slab, form.invoiceType).cgstAmount;
    }, 0);
    const sgstAmount = form.services.reduce((sum, s) => {
        const amount = Number(s.amount) || 0;
        const slab = form.invoiceType === 'CF' ? '' : (s.taxSlab || selectedGstSlab);
        return sum + getTaxBreakdown(amount, slab, form.invoiceType).sgstAmount;
    }, 0);
    const igstAmount = form.services.reduce((sum, s) => {
        const amount = Number(s.amount) || 0;
        const slab = form.invoiceType === 'CF' ? '' : (s.taxSlab || selectedGstSlab);
        return sum + getTaxBreakdown(amount, slab, form.invoiceType).igstAmount;
    }, 0);
    const totalPending = parseFloat(Math.max(0, form.totalAmount - (Number(form.advancePaidAmount) || 0)).toFixed(2));
    const serviceDescriptionOptions = useMemo(
        () => Array.from(
            new Set(
                serviceOptions
                    .map(item => item.description?.trim())
                    .filter((desc): desc is string => Boolean(desc))
            )
        ),
        [serviceOptions]
    );

    const updateService = (index: number, field: keyof InvoiceService, value: string | number) => {
        setForm(prev => {
            const updated = [...prev.services];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, services: updated };
        });
    };

    const addService = () => {
        if (isAdd && form.services.length >= MAX_SERVICES) {
            setAlert({
                show: true,
                type: 'error',
                message: `Maximum ${MAX_SERVICES} services allowed in one invoice`,
            });
            return;
        }

        setForm(prev => ({
            ...prev,
            services: [
                ...prev.services,
                {
                    ...emptyService(),
                    taxSlab: prev.invoiceType === 'CF' ? '' : (selectedGstSlab || 'IGST_0'),
                },
            ],
        }));
    };

    const removeService = (index: number) => {
        if (form.services.length <= 1) return;
        setForm(prev => ({ ...prev, services: prev.services.filter((_, i) => i !== index) }));
    };

    const validate = (): boolean => {
        const errs: Record<string, string> = {};
        if (!form.clientId) errs.clientId = 'Client is required';
        if (!form.invoiceDate) errs.invoiceDate = 'Invoice date is required';
        if (form.invoiceType === 'INV' && !selectedGstSlab) errs.gstSlab = 'GST Tax Slab is required';
        if (form.services.length === 0) {
            errs.services = 'At least 1 service is required';
        } else {
            form.services.forEach((s, i) => {
                if (!s.description.trim()) errs[`service_desc_${i}`] = 'Description is required';
                if (!s.amount || Number(s.amount) <= 0) errs[`service_amount_${i}`] = 'Amount is required';
            });
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setIsLoading(true);
        try {
            const normalizedDescriptionSet = new Set(
                serviceOptions.map((item) => normalizeServiceDescription(String(item.description || '')))
            );
            const newDescriptions = Array.from(
                new Set(
                    form.services
                        .map((item) => String(item.description || '').trim())
                        .filter(Boolean)
                )
            ).filter((description) => !normalizedDescriptionSet.has(normalizeServiceDescription(description)));

            const normalizedServices = form.services.map((s) => {
                const amount = Number(s.amount) || 0;
                const slab = form.invoiceType === 'CF' ? 'IGST_0' : (s.taxSlab || selectedGstSlab || 'IGST_0');
                const tax = getTaxBreakdown(amount, slab, form.invoiceType);
                return {
                    accountId: s.accountId ?? null,
                    description: String(s.description || '').trim(),
                    ratePerUnitHour: s.ratePerUnitHour || null,
                    unitHour: s.unitHour || null,
                    amount,
                    taxSlab: slab,
                    igstPercent: tax.igstPercent,
                    cgstPercent: tax.cgstPercent,
                    sgstPercent: tax.sgstPercent,
                    igstAmount: tax.igstAmount,
                    cgstAmount: tax.cgstAmount,
                    sgstAmount: tax.sgstAmount,
                    taxAmount: tax.taxAmount,
                    totalAmount: tax.totalAmount,
                };
            });

            const primaryYear = form.yearForReminder[0] || '';
            const yearForReminder = primaryYear ? Number(primaryYear.replace(/[^\d]/g, '')) || null : null;

            // Build the full invoice number from the editable sequence
            const buildInvoiceNumber = () => {
                const prefix = form.invoiceType === 'CF' ? 'CF' : 'INV';
                const date = form.invoiceDate ? new Date(form.invoiceDate) : new Date();
                const month = date.getMonth() + 1;
                const year = date.getFullYear();
                const fyStart = month >= 4 ? year : year - 1;
                const fyEnd = fyStart + 1;
                const fySuffix = `${String(fyStart).slice(-2)}-${String(fyEnd).slice(-2)}`;
                const seq = String(Number(invoiceSeq) || 1).padStart(4, '0');
                return `${prefix}-${seq}/${fySuffix}`;
            };

            const payload: any = {
                type: form.invoiceType,
                invoiceNumber: buildInvoiceNumber(),
                clientId: form.clientId,
                services: normalizedServices,
                totalDiscount: Number(form.totalDiscount) || 0,
                advancePaidAmount: Number(form.advancePaidAmount) || 0,
                totalTax: Number(form.totalTax) || 0,
                totalAmount: Number(form.totalAmount) || 0,
                totalPendingAmount: totalPending,
                invoiceDate: form.invoiceDate,
                yearForReminder,
                optionForReminder: form.optionForReminder.length ? form.optionForReminder.join(', ') : null,
                additionalNote: form.additionalNote || null,
            };

            if (isAdd) {
                await invoiceService.create(payload);
            } else {
                await invoiceService.update(id!, payload);
            }

            if (newDescriptions.length > 0) {
                await Promise.allSettled(
                    newDescriptions.map((description) => serviceService.create({ description }))
                );
                fetchServiceOptions();
            }

            setAlert({ show: true, type: 'success', message: isAdd ? 'Invoice created successfully' : 'Invoice updated successfully' });
            setTimeout(() => navigate(backTo, { state: backState }), 1000);
        } catch (err: any) {
            const msg = err.response?.data?.message || err.message || 'An error occurred';
            setAlert({ show: true, type: 'error', message: msg });
        } finally {
            setIsLoading(false);
        }
    };

    const sectionLabel = (text: string) => (
        <p className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9] mb-4">{text}</p>
    );

    return (
        <div className="add-edit-admin-container p-[40px]">
            <div className="add-edit-admin-content">
                <div className="form-wrapper max-w-5xl">

                    <div className="-ml-4 mb-2">
                        <Link to={backTo} state={backState} className="back-link">
                            <ArrowBack />
                            <span className="ml-2">Back to Invoices</span>
                        </Link>
                    </div>

                    <div className="title text-2xl sm:text-5xl">
                        {isAdd ? 'Create Invoice' : 'Edit Invoice'}
                    </div>

                    <div className="form-card mt-8">
                        {isLoadingInvoice ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <CircularProgress size={50} sx={{ color: '#6366f1' }} />
                                <p className="mt-4 text-gray-600 font-medium">Loading invoice details...</p>
                            </div>
                        ) : (
                            <>
                                {alert.show && (
                                    <Alert severity={alert.type} className="mb-8 rounded-xl" onClose={() => setAlert(a => ({ ...a, show: false }))}>
                                        {alert.message}
                                    </Alert>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-6">

                                    {/* Client Company */}
                                    <div>
                                        {sectionLabel('Client Information')}
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    Client Company <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    value={form.clientId}
                                                    onChange={e => {
                                                        setForm(f => ({ ...f, clientId: e.target.value }));
                                                        if (errors.clientId) setErrors(er => ({ ...er, clientId: '' }));
                                                    }}
                                                    error={!!errors.clientId}
                                                    helperText={errors.clientId}
                                                    SelectProps={{ displayEmpty: true }}
                                                    sx={fieldStyles}
                                                >
                                                    <MenuItem value="" disabled>Select Client Company</MenuItem>
                                                    {clients.map((c, index) => {
                                                        const clientValue = c._id || (c as any).id || '';
                                                        const clientKey = clientValue || `client-${index}-${c.companyName || 'unknown'}`;
                                                        return (
                                                            <MenuItem key={clientKey} value={clientValue}>
                                                                {c.displayName || c.companyName || c.ownerName || '—'}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    Invoice Date <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    fullWidth
                                                    type="date"
                                                    value={form.invoiceDate}
                                                    onChange={e => {
                                                        setForm(f => ({ ...f, invoiceDate: e.target.value }));
                                                        if (errors.invoiceDate) setErrors(er => ({ ...er, invoiceDate: '' }));
                                                    }}
                                                    error={!!errors.invoiceDate}
                                                    helperText={errors.invoiceDate}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={fieldStyles}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    Invoice Type <span className="text-red-500">*</span>
                                                </label>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    value={form.invoiceType}
                                                    onChange={e => {
                                                        const t = e.target.value as InvoiceType;
                                                        setForm(f => ({
                                                            ...f,
                                                            invoiceType: t,
                                                            services: f.services.map(s => ({
                                                                ...s,
                                                                taxSlab: t === 'CF' ? '' : (s.taxSlab || selectedGstSlab || 'IGST_0'),
                                                            })),
                                                        }));
                                                        if (t === 'CF') {
                                                            setSelectedGstSlab('');
                                                        } else if (!selectedGstSlab) {
                                                            setSelectedGstSlab('IGST_0');
                                                        }
                                                    }}
                                                    SelectProps={{ displayEmpty: true }}
                                                    sx={fieldStyles}
                                                >
                                                    {INVOICE_TYPE_OPTIONS.map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                    Invoice Number
                                                </label>
                                                {(() => {
                                                    const d = form.invoiceDate ? new Date(form.invoiceDate) : new Date();
                                                    const m2 = d.getMonth() + 1;
                                                    const y2 = d.getFullYear();
                                                    const s = m2 >= 4 ? y2 : y2 - 1;
                                                    const fySuffix = `${String(s).slice(-2)}-${String(s + 1).slice(-2)}`;
                                                    const prefix = form.invoiceType === 'CF' ? 'CF' : 'INV';
                                                    return (
                                                        <OutlinedInput
                                                            fullWidth
                                                            type="number"
                                                            value={invoiceSeq}
                                                            onChange={e => {
                                                                const val = e.target.value.replace(/[^0-9]/g, '');
                                                                setInvoiceSeq(val);
                                                            }}
                                                            onWheel={e => (e.target as HTMLInputElement).blur()}
                                                            onKeyDown={e => {
                                                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                                                            }}
                                                            disabled={nextNumberLoading}
                                                            placeholder={nextNumberLoading ? 'Loading...' : '0001'}
                                                            startAdornment={
                                                                <span style={{
                                                                    paddingRight: '10px',
                                                                    borderRight: '1.5px solid #e2e8f0',
                                                                    marginRight: '10px',
                                                                    color: '#0369a1',
                                                                    fontWeight: 700,
                                                                    fontSize: '14px',
                                                                    whiteSpace: 'nowrap',
                                                                    userSelect: 'none',
                                                                }}>
                                                                    {prefix}
                                                                </span>
                                                            }
                                                            endAdornment={
                                                                <span style={{
                                                                    paddingLeft: '10px',
                                                                    borderLeft: '1.5px solid #e2e8f0',
                                                                    marginLeft: '4px',
                                                                    color: '#64748b',
                                                                    fontWeight: 600,
                                                                    fontSize: '13px',
                                                                    whiteSpace: 'nowrap',
                                                                    userSelect: 'none',
                                                                }}>
                                                                    /{fySuffix}
                                                                </span>
                                                            }
                                                            inputProps={{
                                                                min: 1,
                                                                step: 1,
                                                                style: {
                                                                    textAlign: 'center',
                                                                    fontWeight: 700,
                                                                    letterSpacing: '3px',
                                                                    fontSize: '15px',
                                                                    color: '#1f2937',
                                                                },
                                                            }}
                                                            sx={{
                                                                borderRadius: '10px',
                                                                backgroundColor: '#fff',
                                                                '& fieldset': { borderColor: '#e2e8f0' },
                                                                '&:hover fieldset': { borderColor: '#cbd5e1' },
                                                                '&.Mui-focused fieldset': { borderColor: '#0EA5E9' },
                                                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                                                    WebkitAppearance: 'none',
                                                                    margin: 0,
                                                                },
                                                                '& input[type=number]': {
                                                                    MozAppearance: 'textfield',
                                                                },
                                                            }}
                                                        />
                                                    );
                                                })()}
                                                <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                                                    {isAdd ? 'Auto-suggested — you can change it' : 'Edit sequence number if needed'}
                                                </p>
                                            </Grid>
                                        </Grid>
                                    </div>

                                    {/* Invoice Services */}
                                    <div>
                                        {sectionLabel('Invoice Services')}
                                        <div
                                            style={{
                                                border: '2px solid #0EA5E9',
                                                borderRadius: '10px',
                                                padding: '16px',
                                            }}
                                        >
                                            {form.services.map((svc, idx) => (
                                                <div
                                                    key={idx}
                                                    style={{
                                                        border: '1px solid rgba(14, 165, 233, 0.35)',
                                                        borderRadius: '8px',
                                                        padding: '12px',
                                                        marginBottom: idx < form.services.length - 1 ? '12px' : '0',
                                                        backgroundColor: 'rgba(14, 165, 233, 0.03)',
                                                    }}
                                                >
                                                    <Grid container spacing={2} alignItems="flex-start">
                                                        <Grid item xs={12} sm={3}>
                                                            <label className="block text-xs font-semibold text-gray-500 mb-1">
                                                                Description
                                                            </label>
                                                            <div style={{ position: 'relative' }}>
                                                                <Autocomplete
                                                                    freeSolo
                                                                    selectOnFocus
                                                                    clearOnBlur={false}
                                                                    handleHomeEndKeys
                                                                    loading={isServiceOptionsLoading}
                                                                    options={serviceDescriptionOptions}
                                                                    value={svc.description || ''}
                                                                    onInputChange={(_, value) => updateService(idx, 'description', value)}
                                                                    onChange={(_, value) => updateService(idx, 'description', String(value || ''))}
                                                                    filterOptions={(options, state) => {
                                                                        const query = state.inputValue.trim().toLowerCase();
                                                                        if (!query) return options.slice(0, 10);
                                                                        return options.filter(option => option.toLowerCase().includes(query)).slice(0, 10);
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            fullWidth
                                                                            multiline
                                                                            minRows={2}
                                                                            placeholder="Enter Description"
                                                                            error={!!errors[`service_desc_${idx}`]}
                                                                            helperText={errors[`service_desc_${idx}`] || 'Type to see suggestions or add new service'}
                                                                            inputProps={{
                                                                                ...params.inputProps,
                                                                                style: { resize: 'vertical', overflow: 'auto' },
                                                                            }}
                                                                            sx={{
                                                                                ...fieldStyles,
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    ...fieldStyles['& .MuiOutlinedInput-root'],
                                                                                    height: 'auto',
                                                                                    alignItems: 'flex-start',
                                                                                },
                                                                                '& .MuiInputBase-inputMultiline': {
                                                                                    resize: 'vertical !important',
                                                                                    overflow: 'auto !important',
                                                                                },
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '3px',
                                                                    marginTop: '3px',
                                                                    color: '#94a3b8',
                                                                    fontSize: '10px',
                                                                    userSelect: 'none',
                                                                    justifyContent: 'flex-end',
                                                                    paddingRight: '2px',
                                                                }}>
                                                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <line x1="2" y1="9" x2="9" y2="2" stroke="#94a3b8" strokeWidth="1.2" strokeLinecap="round" />
                                                                        <line x1="5" y1="9" x2="9" y2="5" stroke="#94a3b8" strokeWidth="1.2" strokeLinecap="round" />
                                                                        <line x1="8" y1="9" x2="9" y2="8" stroke="#94a3b8" strokeWidth="1.2" strokeLinecap="round" />
                                                                    </svg>
                                                                    <span>Drag to resize</span>
                                                                </div>
                                                            </div>
                                                        </Grid>
                                                        <Grid item xs={12} sm={2}>
                                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Amount</label>
                                                            <TextField
                                                                fullWidth
                                                                type="number"
                                                                placeholder="Enter Amount"
                                                                value={svc.amount || ''}
                                                                disabled={!isAdd && isSalesInvoice}
                                                                onChange={e => {
                                                                    updateService(idx, 'amount', e.target.value);
                                                                    if (errors[`service_amount_${idx}`]) setErrors(er => ({ ...er, [`service_amount_${idx}`]: '' }));
                                                                }}
                                                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                inputProps={{ min: 0, step: 'any' }}
                                                                error={!!errors[`service_amount_${idx}`]}
                                                                helperText={errors[`service_amount_${idx}`]}
                                                                sx={fieldStyles}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={3}>
                                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Rate/Unit Hour</label>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter Rate (e.g. 1000/hr)"
                                                                value={svc.ratePerUnitHour || ''}
                                                                disabled={!isAdd && isSalesInvoice}
                                                                onChange={e => updateService(idx, 'ratePerUnitHour', e.target.value)}
                                                                sx={fieldStyles}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={3}>
                                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Unit/Hour</label>
                                                            <TextField
                                                                fullWidth
                                                                placeholder="Enter Unit/Hour"
                                                                value={svc.unitHour || ''}
                                                                disabled={!isAdd && isSalesInvoice}
                                                                onChange={e => updateService(idx, 'unitHour', e.target.value)}
                                                                sx={fieldStyles}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: '28px !important' }}>
                                                            <IconButton
                                                                onClick={() => removeService(idx)}
                                                                disabled={form.services.length <= 1}
                                                                sx={{
                                                                    backgroundColor: '#dc2626',
                                                                    color: '#fff',
                                                                    borderRadius: '6px',
                                                                    '&:hover': { backgroundColor: '#b91c1c' },
                                                                    '&.Mui-disabled': { backgroundColor: '#d1d5db', color: '#9ca3af' },
                                                                    width: 36,
                                                                    height: 36,
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Grid>
                                                    </Grid>
                                                </div>
                                            ))}

                                            <div className="mt-3">
                                                {errors.services && (
                                                    <p className="text-xs text-red-600 mb-2">{errors.services}</p>
                                                )}
                                                <AppButton
                                                    type="button"
                                                    variant="primary"
                                                    startIcon={<AddIcon />}
                                                    onClick={addService}
                                                    disabled={isAdd && form.services.length >= MAX_SERVICES}
                                                    sx={{ px: 2, py: 1, fontSize: '13px' }}
                                                >
                                                    Add Service
                                                </AppButton>
                                                {isAdd && (
                                                    <p className="text-xs text-gray-500 mt-2">
                                                        Max {MAX_SERVICES} services allowed per invoice
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tax, Discount, Totals */}
                                    <div>
                                        {sectionLabel('Tax & Totals')}
                                        <Grid container spacing={3}>
                                            {/* GST Slab — only for INV type */}
                                            {form.invoiceType === 'INV' ? (
                                                <Grid item xs={12} md={4}>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                        GST Tax Slab
                                                    </label>
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        value={selectedGstSlab}
                                                        disabled={!isAdd && isSalesInvoice}
                                                        onChange={e => {
                                                            const slab = e.target.value as GstSlab | '';
                                                            setSelectedGstSlab(slab);
                                                            if (errors.gstSlab) setErrors(er => ({ ...er, gstSlab: '' }));
                                                            setForm(f => ({
                                                                ...f,
                                                                services: f.services.map(s => ({ ...s, taxSlab: slab })),
                                                            }));
                                                        }}
                                                        SelectProps={{ displayEmpty: true }}
                                                        error={!!errors.gstSlab}
                                                        helperText={errors.gstSlab}
                                                        sx={fieldStyles}
                                                    >
                                                        {GST_SLAB_OPTIONS.map(opt => (
                                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>
                                            ) : (
                                                <Grid item xs={12} md={4}>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                        GST Tax Slab
                                                    </label>
                                                    <TextField
                                                        fullWidth
                                                        value="No GST — Cash Bill"
                                                        disabled
                                                        sx={{
                                                            ...fieldStyles,
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                backgroundColor: '#fef9c3',
                                                                color: '#92400e',
                                                                fontStyle: 'italic',
                                                            },
                                                        }}
                                                    />
                                                </Grid>
                                            )}

                                            {/* GST Breakdown (shown when CGST+SGST selected) */}
                                            {form.invoiceType === 'INV' && selectedGstSlab === 'CGST_SGST_9' && (
                                                <>
                                                    <Grid item xs={12} md={4}>
                                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">CGST (9%)</label>
                                                        <TextField
                                                            fullWidth
                                                            value={cgstAmount}
                                                            disabled
                                                            sx={{ ...fieldStyles, '& .MuiInputBase-input.Mui-disabled': { backgroundColor: '#f1f5f9', color: '#374151' } }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">SGST (9%)</label>
                                                        <TextField
                                                            fullWidth
                                                            value={sgstAmount}
                                                            disabled
                                                            sx={{ ...fieldStyles, '& .MuiInputBase-input.Mui-disabled': { backgroundColor: '#f1f5f9', color: '#374151' } }}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                            {form.invoiceType === 'INV' && selectedGstSlab === 'IGST_18' && (
                                                <Grid item xs={12} md={4}>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">IGST (18%)</label>
                                                    <TextField
                                                        fullWidth
                                                        value={igstAmount}
                                                        disabled
                                                        sx={{ ...fieldStyles, '& .MuiInputBase-input.Mui-disabled': { backgroundColor: '#f1f5f9', color: '#374151' } }}
                                                    />
                                                </Grid>
                                            )}

                                            {/* Total Tax */}
                                            <Grid item xs={12} md={4}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Total Tax</label>
                                                <TextField
                                                    fullWidth
                                                    value={form.totalTax}
                                                    disabled
                                                    sx={{ ...fieldStyles, '& .MuiInputBase-input.Mui-disabled': { backgroundColor: '#f1f5f9', color: '#374151' } }}
                                                />
                                            </Grid>

                                            {/* Total Discount */}
                                            <Grid item xs={12} md={4}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Total Discount</label>
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    value={form.totalDiscount || ''}
                                                    disabled={!isAdd && isSalesInvoice}
                                                    onChange={e => setForm(f => ({ ...f, totalDiscount: Number(e.target.value) || 0 }))}
                                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    inputProps={{ min: 0, step: 'any' }}
                                                    sx={fieldStyles}
                                                />
                                            </Grid>

                                            {/* Advance Paid Amount */}
                                            <Grid item xs={12} md={4}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Advance Paid Amount</label>
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    value={form.advancePaidAmount || ''}
                                                    disabled={!isAdd && isSalesInvoice}
                                                    onChange={e => setForm(f => ({ ...f, advancePaidAmount: Number(e.target.value) || 0 }))}
                                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    inputProps={{ min: 0, step: 'any' }}
                                                    sx={fieldStyles}
                                                />
                                            </Grid>

                                            {/* Total Amount */}
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Total Amount</label>
                                                <TextField
                                                    fullWidth
                                                    value={form.totalAmount}
                                                    disabled
                                                    sx={{ ...fieldStyles, '& .MuiInputBase-input.Mui-disabled': { backgroundColor: '#f1f5f9', color: '#374151', fontWeight: 700 } }}
                                                />
                                            </Grid>

                                            {/* Total Pending Amount */}
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Total Pending Amount</label>
                                                <TextField
                                                    fullWidth
                                                    value={totalPending}
                                                    disabled
                                                    sx={{ ...fieldStyles, '& .MuiInputBase-input.Mui-disabled': { backgroundColor: '#f1f5f9', color: '#374151', fontWeight: 700 } }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </div>

                                    {/* Reminder Settings */}
                                    <div>
                                        {sectionLabel('Reminder Settings')}
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Year For Reminder</label>
                                                <FormControl fullWidth sx={{ ...fieldStyles, mt: '0 !important' }}>
                                                    <Select
                                                        multiple
                                                        displayEmpty
                                                        value={form.yearForReminder}
                                                        onChange={e => setForm(f => ({ ...f, yearForReminder: e.target.value as string[] }))}
                                                        input={<OutlinedInput />}
                                                        renderValue={(selected) =>
                                                            selected.length === 0
                                                                ? <span style={{ color: '#9ca3af' }}>Select Some Options</span>
                                                                : selected.join(', ')
                                                        }
                                                        sx={{
                                                            borderRadius: '10px',
                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0EA5E9' },
                                                        }}
                                                    >
                                                        {YEAR_OPTIONS.map(opt => (
                                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Option For Reminder</label>
                                                <FormControl fullWidth sx={{ ...fieldStyles, mt: '0 !important' }}>
                                                    <Select
                                                        multiple
                                                        displayEmpty
                                                        value={form.optionForReminder}
                                                        onChange={e => setForm(f => ({ ...f, optionForReminder: e.target.value as string[] }))}
                                                        input={<OutlinedInput />}
                                                        renderValue={(selected) =>
                                                            selected.length === 0
                                                                ? <span style={{ color: '#9ca3af' }}>Select Some Options</span>
                                                                : selected.join(', ')
                                                        }
                                                        sx={{
                                                            borderRadius: '10px',
                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0EA5E9' },
                                                        }}
                                                    >
                                                        {REMINDER_OPTIONS.map(opt => (
                                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </div>

                                    {/* Additional Note */}
                                    <div>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Additional Note</label>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    value={form.additionalNote}
                                                    onChange={e => setForm(f => ({ ...f, additionalNote: e.target.value }))}
                                                    placeholder="Enter additional note..."
                                                    sx={{
                                                        ...fieldStyles,
                                                        '& .MuiOutlinedInput-root': {
                                                            ...fieldStyles['& .MuiOutlinedInput-root'],
                                                            height: 'auto',
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
                                        <AppButton
                                            type="button"
                                            variant="cancel"
                                            onClick={() => navigate(backTo, { state: backState })}
                                            sx={{ px: 4 }}
                                        >
                                            Cancel
                                        </AppButton>
                                        <AppButton
                                            type="submit"
                                            variant="primary"
                                            startIcon={<SaveIcon />}
                                            loading={isLoading}
                                            sx={{ px: 5 }}
                                        >
                                            {isAdd ? 'Save' : 'Update Invoice'}
                                        </AppButton>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Snackbar
                open={alert.show}
                autoHideDuration={4000}
                onClose={() => setAlert(a => ({ ...a, show: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={alert.type} variant="filled" className="rounded-xl">
                    {alert.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

